// Example stories data
export const exampleStories = {
  1: {
    id: 1,
    title: "Alex and the Space Puzzle",
    subtitle: "A Confidence Story",
    author: "Massal AI",
    need: "Building confidence with hard problems",
    description: "Alex turns a big, intimidating puzzle into a space mission about patience, curiosity, and asking for help when something feels too hard.",
    fullDescription: "When Alex finds a strange, glowing puzzle box in their backyard, it becomes the doorway to the International Space Station Cosmos. There, Alex meets Captain Nova and Zip, a small alien who needs help finding the way home. Each cosmic riddle feels difficult at first, but Alex learns to slow down, try one step at a time, and ask trusted friends for support. The adventure still teaches space, gravity, and simple STEM ideas, but the heart of the story is confidence: hard things become possible when a child feels safe enough to keep trying.",
    age: "7-8",
    theme: "Educational",
    style: "Pastel Crayon",
    characters: 2,
    coverImage: "/images/book-covers/alex_space.png",
    childPhoto: "/images/face/alex_photo.png",
    pdfUrl: "/pdfs/alex_compressed.pdf",
    rating: 4.8,
    reads: 0,
    galleryImages: [
      "/images/stories/space-puzzle/space-station.jpg",
      "/images/stories/space-puzzle/meeting-zip.jpg",
      "/images/stories/space-puzzle/cosmic-puzzles.jpg",
      "/images/stories/space-puzzle/exploring-planets.jpg",
      "/images/stories/space-puzzle/journey-home.jpg"
    ],
    characterDetails: [
      {
        name: "Alex",
        description: "A curious young explorer with a gift for solving puzzles",
        image: "/images/characters/alex-explorer.jpg",
        traits: ["Curious", "Logical", "Brave"]
      },
      {
        name: "Captain Nova",
        description: "A wise astronaut who guides space adventures",
        image: "/images/characters/captain-nova.jpg",
        traits: ["Wise", "Experienced", "Helpful"]
      },
      {
        name: "Zip",
        description: "A friendly alien who needs help finding their way home",
        image: "/images/characters/zip-alien.jpg",
        traits: ["Friendly", "Lost", "Grateful"]
      }
    ],
    highlights: [
      "Builds confidence around hard problems",
      "Teaches gentle STEM concepts",
      "Develops problem-solving skills",
      "Models asking for help"
    ],
    storyPreview: {
      pageCount: 32,
      samplePages: [
        {
          pageNumber: 8,
          text: "As Alex floated in zero gravity for the first time, Captain Nova smiled. 'Welcome to space, young explorer. Are you ready to help us solve the greatest puzzle in the galaxy?'",
          image: "/images/stories/space-puzzle/page-8.jpg"
        },
        {
          pageNumber: 20,
          text: "Alex looked through the telescope at Zip's home planet twinkling in the distance. 'Every puzzle we solve together brings us one step closer to helping our friend get home,' Alex said with determination.",
          image: "/images/stories/space-puzzle/page-20.jpg"
        }
      ]
    },
    educationalValue: {
      skills: ["Problem-solving", "Logical thinking", "STEM concepts", "Spatial reasoning"],
      vocabulary: ["Astronaut", "Galaxy", "Gravity", "Telescope", "Navigation"],
      moralLessons: ["Curiosity leads to discovery", "Helping others is rewarding", "Science makes the impossible possible"]
    },
    creationStory: "This space adventure was created for children who freeze when a task looks too big. The science setting keeps the story exciting, while Alex's emotional journey shows that persistence can be calm, supported, and playful.",
    similarStories: [3, 4, 9]
  },
  2: {
    id: 2,
    title: "Captain Mary and the Ocean Adventure",
    subtitle: "An Underwater Adventure",
    author: "Massal AI",
    need: "Caring for nature and taking responsibility",
    description: "Mary discovers a magical underwater world when she finds a special diving helmet that lets her breathe underwater and communicate with sea creatures.",
    fullDescription: "Mary has always loved the ocean, but she never imagined she could explore it like this! When she finds a mysterious diving helmet washed up on the beach, Mary discovers it has magical powers that allow her to breathe underwater and understand what sea creatures are saying. Diving beneath the waves, Mary meets Coral the wise sea turtle, Splash the playful dolphin, and many other ocean friends. But the underwater kingdom is in trouble - pollution from the surface is threatening their home. Mary must work with her new sea friends to solve the mystery of the disappearing coral reef, learn about ocean conservation, and help protect the beautiful underwater world she has come to love.",
    age: "3-5",
    theme: "Adventure",
    style: "3D-Animation",
    characters: 1,
    coverImage: "/images/book-covers/mary.png",
    childPhoto: "/images/face/mary_photo.png",
    pdfUrl: "/pdfs/mary_compressed.pdf",
    rating: 4.9,
    reads: 0,
    galleryImages: [
      "/images/stories/ocean-adventure/diving-discovery.jpg",
      "/images/stories/ocean-adventure/meeting-sea-friends.jpg",
      "/images/stories/ocean-adventure/coral-reef.jpg",
      "/images/stories/ocean-adventure/ocean-cleanup.jpg",
      "/images/stories/ocean-adventure/protecting-ocean.jpg"
    ],
    characterDetails: [
      {
        name: "Mary",
        description: "A brave 8-year-old who loves the ocean and marine life",
        image: "/images/characters/mary-portrait.jpg",
        traits: ["Brave", "Caring", "Adventurous"]
      },
      {
        name: "Coral",
        description: "A wise old sea turtle who protects the reef",
        image: "/images/characters/coral-turtle.jpg",
        traits: ["Wise", "Ancient", "Protective"]
      },
      {
        name: "Splash",
        description: "A playful dolphin who becomes Mary's guide",
        image: "/images/characters/splash-dolphin.jpg",
        traits: ["Playful", "Loyal", "Energetic"]
      }
    ],
    highlights: [
      "Exciting underwater adventure",
      "Teaches ocean conservation",
      "Promotes environmental awareness",
      "Celebrates marine life diversity"
    ],
    storyPreview: {
      pageCount: 28,
      samplePages: [
        {
          pageNumber: 6,
          text: "As Mary put on the magical diving helmet, she felt a tingle of excitement. Taking a deep breath, she stepped into the ocean and gasped in wonder as she could suddenly breathe underwater!",
          image: "/images/stories/ocean-adventure/page-6.jpg"
        },
        {
          pageNumber: 15,
          text: "'The ocean needs a friend like you, Mary,' said Coral the sea turtle. 'Will you help us protect our home so future generations can enjoy its beauty?'",
          image: "/images/stories/ocean-adventure/page-15.jpg"
        }
      ]
    },
    educationalValue: {
      skills: ["Environmental awareness", "Marine biology basics", "Problem-solving", "Conservation concepts"],
      vocabulary: ["Ocean", "Conservation", "Marine", "Coral reef", "Ecosystem"],
      moralLessons: ["Protect the environment", "Every action matters", "Friendship crosses all boundaries"]
    },
    creationStory: "This underwater adventure was inspired by children's fascination with ocean life and the importance of teaching environmental conservation from an early age. Mary represents every child's potential to make a positive impact on our planet.",
    similarStories: [5, 6, 8]
  },
  3: {
    id: 3,
    title: "Super Oliver: Guardian of the City",
    subtitle: "Guardian of the City",
    author: "Massal AI",
    need: "Feeling brave while helping others",
    description: "Oliver is no ordinary boy. Every day, he watches over his futuristic city as Super Oliver, dreaming of saving people and keeping his home safe.",
    fullDescription: "Oliver lives in a futuristic metropolis where he secretly becomes Super Oliver to protect his home. Each morning, he senses when the city needs him, readies his gear, and feels his emblem glow with excitement. Through adventures across towering skyscrapers, neon-lit streets, and hidden corners of the city, Oliver learns about responsibility, bravery, and the importance of helping others. Along the way, he confronts challenges, rescues citizens in need, and discovers that true heroism comes from compassion and courage.",
    age: "6-8",
    theme: "Adventure",
    style: "3D-Animation",
    characters: 1,
    coverImage: "/images/book-covers/oliver.png",
    childPhoto: "/images/face/oliver_photo.png",
    pdfUrl: "/pdfs/oliver_compressed.pdf",
    rating: 4.6,
    reads: 0,
    galleryImages: [
      "/images/stories/super-oliver/guardian-gear.jpg",
      "/images/stories/super-oliver/city-patrol.jpg",
      "/images/stories/super-oliver/rescue-scene.jpg",
      "/images/stories/super-oliver/emblem-glow.jpg",
      "/images/stories/super-oliver/victory.jpg"
    ],
    characterDetails: [
      {
        name: "Oliver",
        description: "An ordinary boy by day and the courageous protector of his futuristic city by night",
        image: "/images/characters/oliver-superhero.jpg",
        traits: ["Brave", "Responsible", "Compassionate"]
        }
    ],
    highlights: [
      "Features a young superhero protecting a futuristic city",
      "Emphasizes responsibility, bravery, and compassion",
      "Encourages helping others and civic duty",
      "Promotes problem-solving and quick thinking in emergencies"
    ],
    storyPreview: {
      pageCount: 28,
      samplePages: [
        {
          pageNumber: 4,
          text: "Oliver is no ordinary boy. Every day, he watches over his futuristic city as Super Oliver. He dreams of saving people and keeping his home safe.",
          image: "/images/stories/super-oliver/page-4.jpg"
        },
        {
          pageNumber: 7,
          text: "This morning, Oliver senses that the city needs him. He readies his gear and checks his emblem's glow. His heart beats with excitement as he prepares for adventure.",
          image: "/images/stories/super-oliver/page-7.jpg"
        }
        ]
    },
    educationalValue: {
      skills: ["Civic awareness", "Problem-solving", "Responsibility", "Quick decision-making"],
      vocabulary: ["Hero", "City", "Responsibility", "Courage", "Protector"],
      moralLessons: ["Helping others is heroic", "Responsibility comes with power", "Courage in the face of challenges"]
    },
    creationStory: "Inspired by the idea of a young hero who steps up to protect his community, this story celebrates bravery and the importance of caring for others.",
    similarStories: [1, 8, 9]
  },
  4: {
    id: 4,
    title: "Mina and the Secret Library",
    subtitle: "A Mystery Adventure",
    author: "Massal AI",
    need: "Curiosity and reading confidence",
    description: "Mina discovers a hidden library in her school where books come alive and ancient riddles guard the greatest stories ever told.",
    fullDescription: "Mina has always been curious about the locked door at the end of the school hallway. When she finally finds the key hidden in an old textbook, she discovers something extraordinary - a secret library where books float through the air, characters step out of their stories, and ancient riddles protect the most precious tales ever written. But the library is in danger of being forgotten forever. Mina must solve the guardian's riddles, help the story characters return to their books, and learn the true power of stories before the magic is lost to time.",
    age: "6-8",
    theme: "Educational",
    style: "Fantasy Art",
    characters: 1,
    coverImage: "/images/book-covers/mina.png",
    pdfUrl: "/pdfs/mina_compressed.pdf",
    rating: 4.9,
    reads: 0,
    galleryImages: [
      "/images/stories/secret-library/hidden-entrance.jpg",
      "/images/stories/secret-library/floating-books.jpg",
      "/images/stories/secret-library/ancient-guardians.jpg",
      "/images/stories/secret-library/solving-riddles.jpg",
      "/images/stories/secret-library/saving-knowledge.jpg"
    ],
    characterDetails: [
      {
        name: "Mina",
        description: "A book-loving 8-year-old with exceptional puzzle-solving skills",
        image: "/images/characters/mina-reader.jpg",
        traits: ["Intelligent", "Curious", "Brave"]
      },
      {
        name: "Lexicon",
        description: "An ancient book guardian made of living words",
        image: "/images/characters/lexicon-guardian.jpg",
        traits: ["Ancient", "Wise", "Protective"]
      },
      {
        name: "Quill",
        description: "A magical writing instrument that helps solve riddles",
        image: "/images/characters/magical-quill.jpg",
        traits: ["Helpful", "Magical", "Clever"]
      }
    ],
    highlights: [
      "Promotes love of reading",
      "Develops logical thinking",
      "Teaches value of knowledge",
      "Encourages curiosity and exploration"
    ],
    storyPreview: {
      pageCount: 30,
      samplePages: [
        {
          pageNumber: 6,
          text: "Mina's eyes widened as books began floating around her like gentle butterflies. 'Welcome, young seeker,' spoke a voice made of rustling pages. 'The library has been waiting for someone like you.'",
          image: "/images/stories/secret-library/page-6.jpg"
        },
        {
          pageNumber: 18,
          text: "'The first riddle,' hummed Lexicon, 'is hidden in the story of a brave child who wasn't afraid to ask questions. Do you know where to look, little scholar?'",
          image: "/images/stories/secret-library/page-18.jpg"
        }
      ]
    },
    educationalValue: {
      skills: ["Reading comprehension", "Logical reasoning", "Research skills", "Critical thinking"],
      vocabulary: ["Library", "Ancient", "Riddle", "Guardian", "Knowledge"],
      moralLessons: ["Knowledge is powerful", "Curiosity leads to discovery", "Protect what's valuable"]
    },
    creationStory: "Inspired by children's love of libraries and the magic they feel when surrounded by books. This story celebrates young readers and shows that knowledge truly is a superpower.",
    similarStories: [1, 2, 9]
  },
  5: {
    id: 5,
    title: "Into the Woods: A Tale of Trust",
    subtitle: "An Adventure Story",
    author: "Massal AI",
    need: "Trusting a helper and staying calm",
    description: "Five-year-old Lisa and her loyal golden retriever, Charlie, embark on an adventure into the woods, where they must rely on each other to find their way home after getting lost.",
    fullDescription: "This heartwarming adventure follows Lisa, a brave five-year-old, and her best friend Charlie, a golden retriever with an excellent sense of direction. When a family picnic leads to an unexpected adventure in the deep woods, Lisa and Charlie must work together to find their way home. Along the journey, they meet friendly woodland creatures, learn to trust their instincts, and discover that sometimes getting lost can lead to the most beautiful discoveries. This story teaches children about courage, friendship, and the importance of staying calm in challenging situations.",
    age: "5-8",
    theme: "Adventure",
    style: "3D Animation",
    characters: 2,
    coverImage: "/images/book-covers/alex.png",
    pdfUrl: "/pdfs/alex_compressed.pdf",
    rating: 4.8,
    reads: 0,
    galleryImages: [
      "/images/stories/woods-adventure/lisa-charlie-start.jpg",
      "/images/stories/woods-adventure/forest-exploration.jpg",
      "/images/stories/woods-adventure/meeting-animals.jpg",
      "/images/stories/woods-adventure/finding-way-home.jpg",
      "/images/stories/woods-adventure/happy-reunion.jpg"
    ],
    characterDetails: [
      {
        name: "Lisa",
        description: "A brave and curious 5-year-old who loves adventures",
        image: "/images/characters/lisa-portrait.jpg",
        traits: ["Brave", "Curious", "Kind"]
      },
      {
        name: "Charlie",
        description: "A loyal golden retriever with excellent instincts",
        image: "/images/characters/charlie-portrait.jpg",
        traits: ["Loyal", "Smart", "Protective"]
      }
    ],
    highlights: [
      "Teaches problem-solving skills",
      "Promotes friendship and teamwork",
      "Encourages outdoor exploration",
      "Builds confidence in challenging situations"
    ],
    storyPreview: {
      pageCount: 24,
      samplePages: [
        {
          pageNumber: 3,
          text: "Lisa looked around at the tall trees and realized she had never seen this part of the forest before. 'Don't worry, Charlie,' she said, patting her furry friend. 'We'll find our way home together.'",
          image: "/images/stories/woods-adventure/page-3.jpg"
        },
        {
          pageNumber: 8,
          text: "A wise old owl perched on a branch above them. 'The path home isn't always straight,' hooted the owl. 'Sometimes you must follow your heart to find your way.'",
          image: "/images/stories/woods-adventure/page-8.jpg"
        }
      ]
    },
    educationalValue: {
      skills: ["Problem-solving", "Direction and navigation", "Animal recognition", "Emotional resilience"],
      vocabulary: ["Adventure", "Trust", "Courage", "Navigation", "Friendship"],
      moralLessons: ["Trust your instincts", "Friends help each other", "Stay calm in difficult situations"]
    },
    creationStory: "This story was inspired by real adventures families have shared with us about getting lost during nature walks and finding their way home together. The character of Charlie was based on therapy dogs who help children feel safe and confident.",
    similarStories: [7, 8, 9]
  },
  6: {
    id: 6,
    title: "Ella's Magical Art Adventure",
    subtitle: "A Creative Journey",
    author: "Massal AI",
    need: "Creative confidence",
    description: "Join Ella as she discovers that her drawings come to life, leading her on a colorful adventure through her own artwork.",
    fullDescription: "Ella loves to draw more than anything in the world. Armed with her special set of colored pencils - a gift from her art teacher - she creates beautiful pictures that somehow seem more real than they should be. One rainy afternoon, while drawing in her sketchbook, Ella accidentally drops a tear of frustration onto her latest creation. Suddenly, the drawing begins to shimmer and glow, and before she knows it, Ella is pulled into her own artwork! Inside this magical world, she meets the characters she's drawn and discovers that her creativity has the power to solve problems, help others, and create wonders beyond her imagination.",
    age: "5-9",
    theme: "Fantasy",
    style: "Watercolor",
    characters: 1,
    coverImage: "/images/book-covers/ella.png",
    pdfUrl: "/pdfs/ella_compressed.pdf",
    rating: 4.7,
    reads: 0,
    galleryImages: [
      "/images/stories/art-adventure/ella-drawing.jpg",
      "/images/stories/art-adventure/entering-artwork.jpg",
      "/images/stories/art-adventure/colorful-world.jpg",
      "/images/stories/art-adventure/helping-characters.jpg",
      "/images/stories/art-adventure/creating-magic.jpg"
    ],
    characterDetails: [
      {
        name: "Ella",
        description: "A creative 7-year-old artist with a magical gift",
        image: "/images/characters/ella-artist.jpg",
        traits: ["Creative", "Imaginative", "Helpful"]
      },
      {
        name: "Sketch the Dragon",
        description: "A friendly dragon Ella drew who becomes her guide",
        image: "/images/characters/sketch-dragon.jpg",
        traits: ["Wise", "Gentle", "Artistic"]
      },
      {
        name: "Color Sprites",
        description: "Tiny beings made of pure color who help bring art to life",
        image: "/images/characters/color-sprites.jpg",
        traits: ["Energetic", "Colorful", "Playful"]
      }
    ],
    highlights: [
      "Encourages artistic expression",
      "Celebrates creativity and imagination",
      "Teaches that art can solve problems",
      "Shows the power of believing in yourself"
    ],
    storyPreview: {
      pageCount: 26,
      samplePages: [
        {
          pageNumber: 7,
          text: "Ella watched in amazement as her colored pencil lines began to glow and lift off the paper. 'Don't be afraid,' whispered a gentle voice. 'Your art wants to come alive!'",
          image: "/images/stories/art-adventure/page-7.jpg"
        },
        {
          pageNumber: 15,
          text: "Inside her drawing, everything was possible. Mountains were made of purple crayon strokes, rivers flowed with liquid blue paint, and trees grew leaves of every color Ella had ever imagined.",
          image: "/images/stories/art-adventure/page-15.jpg"
        }
      ]
    },
    educationalValue: {
      skills: ["Artistic expression", "Creative problem-solving", "Color theory", "Imagination development"],
      vocabulary: ["Creative", "Imagination", "Artistic", "Magical", "Expression"],
      moralLessons: ["Believe in your talents", "Creativity has power", "Art brings joy to others"]
    },
    creationStory: "Created from the countless drawings children have shared with us, this story celebrates every child's artistic journey and the magic they see in their own creations.",
    similarStories: [1, 2, 8]
  }
};

export const getStoryById = (id) => {
  return exampleStories[id] || null;
};

export const getAllStories = () => {
  return Object.values(exampleStories);
};
